import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { ShieldCheck, HandHeart, ArrowRight, Globe2 } from "lucide-react";

const CATEGORY_LABELS = {
  ma: "M&A", fleet: "Fleet", aviation: "Aviation", maritime: "Maritime",
  "real-estate": "Real Estate", energy: "Energy", capital: "Capital",
  partnership: "Partnership", talent: "Talent", distressed: "Distressed",
  "user-recorded": "Member-Recorded",
};

export default function ClosedDealsWall() {
  const [data, setData] = useState({ stats: null, deals: [] });

  useEffect(() => { api.get("/exchange/closed-deals").then(r => setData(r.data)); }, []);

  return (
    <>
      <section className="bg-[#064E3B] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24" data-testid="closed-wall-hero">
          <div className="federal-stamp" style={{ borderColor: "#FACC15", color: "#FACC15", background: "rgba(250,204,21,0.08)" }}>
            ★ THE CLOSED DEALS WALL · PUBLIC · ANONYMISED
          </div>
          <h1 className="display-black text-5xl lg:text-7xl uppercase mt-6 leading-[0.95] tracking-tighter max-w-5xl">
            Deals close here.<br /><span className="text-[#FACC15]">Members honour their pledges.</span>
          </h1>
          <p className="text-lg text-white/85 mt-6 max-w-3xl">
            Every transaction that closes on the Available Exchange is recorded — anonymously by reference number — alongside the slice pledged back. Watch the wall grow.
          </p>

          {data.stats && (
            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/15" data-testid="closed-wall-stats">
              {[
                { v: data.stats.deals_closed, l: "Deals Closed" },
                { v: `$${data.stats.total_value_m.toLocaleString()}M`, l: "Total Value" },
                { v: `$${data.stats.total_pledged_m.toLocaleString()}M`, l: "Honour Pledged" },
                { v: `${data.stats.honored_pct}%`, l: "Honour Rate" },
              ].map(s => (
                <div key={s.l} className="bg-[#064E3B] p-6 text-center">
                  <div className="display-black text-3xl lg:text-4xl text-[#FACC15]">{s.v}</div>
                  <div className="mono text-[10px] tracking-[0.22em] uppercase text-white/70 mt-2">{s.l}</div>
                </div>
              ))}
            </div>
          )}

          <div className="flex flex-wrap gap-3 mt-10">
            <Link to="/exchange/honor" className="btn-signal" data-testid="cd-record-cta"><HandHeart className="w-4 h-4" /> Record a Pledge</Link>
            <Link to="/exchange" className="btn-ghost" style={{ color: "#fff", borderColor: "#fff" }}>Back to Exchange</Link>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#D97706]">// The Wall</div>
          <h2 className="display-black text-3xl uppercase text-[#064E3B] mt-2">Latest closures.</h2>

          <div className="mt-8 border border-gray-300 divide-y divide-gray-200" data-testid="closed-wall-list">
            {data.deals.map((d) => (
              <div key={d.id} className="grid grid-cols-1 md:grid-cols-12 gap-4 p-5 hover:bg-gray-50 transition-colors" data-testid={`cd-row-${d.id}`}>
                <div className="md:col-span-1 mono text-[12px] text-[#064E3B] font-bold tracking-wider">{d.ref}</div>
                <div className="md:col-span-5">
                  <div className="display-black text-base uppercase text-[#064E3B] leading-tight">{d.summary}</div>
                  <div className="mono text-[10px] tracking-wider text-gray-500 uppercase mt-1 flex items-center gap-2 flex-wrap">
                    <span className="px-2 py-0.5 bg-[#064E3B] text-white">{CATEGORY_LABELS[d.category] || d.category}</span>
                    <span className="inline-flex items-center gap-1"><Globe2 className="w-3 h-3" /> {d.region}</span>
                  </div>
                </div>
                <div className="md:col-span-2 text-right md:text-left">
                  <div className="mono text-[10px] tracking-wider text-gray-500 uppercase">Deal Value</div>
                  <div className="display-black text-lg text-[#064E3B]">${d.value_m}M</div>
                </div>
                <div className="md:col-span-2">
                  <div className="mono text-[10px] tracking-wider text-gray-500 uppercase">Pledged</div>
                  <div className="display-black text-lg text-[#D97706]">${d.pledged_m}M <span className="text-xs text-gray-500">({d.pledged_pct}%)</span></div>
                </div>
                <div className="md:col-span-2 text-right">
                  <div className="mono text-[10px] tracking-wider text-gray-500 uppercase">Closed</div>
                  <div className="mono text-[11px] text-[#064E3B]">{d.closed_at?.slice(0, 10)}</div>
                  <div className="mono text-[10px] tracking-wider text-emerald-600 mt-1 inline-flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3" /> Honoured
                  </div>
                </div>
              </div>
            ))}
            {data.deals.length === 0 && <div className="p-8 text-center text-gray-500">No deals on the wall yet.</div>}
          </div>

          <div className="mt-10 border border-[#064E3B] bg-gray-50 p-6 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="mono text-[10px] tracking-[0.22em] uppercase text-[#D97706]">// Be on the wall.</div>
              <div className="display-black text-xl uppercase text-[#064E3B] mt-2">Closed a deal with someone you met here?</div>
            </div>
            <Link to="/exchange/honor" className="btn-signal">Record a Pledge <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>
    </>
  );
}
